'use client';

import { FormEvent, ReactNode } from 'react';
import Link, { LinkProps } from 'next/link';
import { css } from '@bts-web/utils-style-engine';

export type HyperLinkButtonBaseVariants =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'ghostInverted'
  | 'accent'
  | 'primaryOnBrand';

type ButtonSizeVariants = 'small' | 'medium' | 'large';

type LinkButtonComponentProps = Omit<LinkProps, 'href'> & {
  size?: ButtonSizeVariants;
  visual?: HyperLinkButtonBaseVariants;
  onClick?: () => void;
  to: string;
  noPadding?: boolean;
  children: ReactNode;
  fullWidth?: boolean;
  className?: string;
  inverse?: boolean;
  disabled?: boolean;
  useHtmlAnchor?: boolean;
  prefetch?: boolean;
};

export const HyperLinkButtonBase: (
  props: LinkButtonComponentProps,
) => React.ReactElement = ({
  size = 'large',
  visual = 'secondary',
  children,
  noPadding = false,
  fullWidth = false,
  disabled = false,
  className,
  inverse,
  to,
  onClick,
  useHtmlAnchor = false,
  ...rest
}) => {
  const preventNavigationIfDisabled = (e: FormEvent<HTMLAnchorElement>) => {
    if (disabled) {
      e.preventDefault();
    }

    onClick?.();
  };

  const ComponentElement = useHtmlAnchor ? 'a' : Link;

  return (
    <ComponentElement
      onClick={preventNavigationIfDisabled}
      role="button"
      href={to}
      data-visual={visual}
      data-inverse={inverse}
      data-disabled={disabled}
      aria-disabled={disabled}
      className={`${css({
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        gap: 'extra_small_2',
        borderEndEndRadius: 'buttonBottomRight',
        borderEndStartRadius: 'buttonTopRight',
        borderStartStartRadius: 'buttonTopLeft',
        borderStartEndRadius: 'buttonBottomLeft',
        cursor: 'pointer',
        ...(fullWidth ? { width: '100%' } : {}),
        boxSizing: 'border-box',
        border: '1px solid transparent',
        transition: 'background-color 0.2s, color 0.2s, border-color 0.2s',
        ...(visual === 'primary' && !inverse
          ? {
              borderColor: 'brand.fill_primary',
              backgroundColor: 'brand.fill_primary',
              color: 'neutrals.text_primary_inverted',
              '&:active': {
                bg: 'brand.fill_pressed',
                color: 'neutrals.text_primary_inverted',
              },
              '&:focus': {
                bg: 'brand.fill_pressed',
                color: 'neutrals.text_primary_inverted',
              },
            }
          : {}),
        ...(visual === 'primary' && inverse
          ? {
              borderColor: 'neutrals.text_primary_inverted',
              color: 'neutrals.fill_primary',
              backgroundColor: 'neutrals.text_primary_inverted',
              '&:hover': {
                backgroundColor: 'neutrals.fill_hovered_inverse',
              },
              '&:active': {
                backgroundColor: 'neutrals.fill_pressed_inverse',
              },
              '&:focus': {
                backgroundColor: 'neutrals.fill_focused_inverse',
              },
            }
          : {}),
        ...(visual === 'secondary'
          ? {
              borderColor: 'neutrals.text_primary',
              bg: 'neutrals.card_fill_primary',
              color: 'neutrals.text_primary',
              '&:active': {
                bg: 'neutrals.card_fill_primary',
                color: 'neutrals.text_primary',
                borderColor: 'neutrals.stroke_primary',
              },
              '&:focus': {
                bg: 'neutrals.fill_focused',
                color: 'neutrals.text_primary',
                borderColor: 'neutrals.stroke_primary',
              },
            }
          : {}),
        ...(visual === 'ghost'
          ? {
              borderColor: 'transparent',
              color: 'neutrals.text_primary',
              '&:active': {
                color: 'neutrals.text_primary',
              },
              '&:focus': {
                color: 'neutrals.text_primary',
              },
            }
          : {}),
        ...(visual === 'ghostInverted'
          ? {
              borderColor: 'transparent',
              color: 'neutrals.text_primary_inverted',
              '&:active': {
                color: 'neutrals.text_primary_inverted',
              },
              '&:focus': {
                color: 'neutrals.text_primary_inverted',
              },
            }
          : {}),
        ...(visual === 'accent' && !inverse
          ? {
              borderColor: 'brand.fill_primary',
              backgroundColor: 'brand.fill_primary',
              color: 'brand.on_fill_primary',
              _hover: {
                backgroundColor: 'brand.fill_focused',
                color: 'brand.fill_primary',
              },
              '&:active:enabled': {
                backgroundColor: 'brand.fill_pressed',
                color: 'brand.on_fill_primary',
              },
              '&:focus:enabled': {
                backgroundColor: 'brand.fill_pressed',
                color: 'brand.on_fill_primary',
              },
            }
          : {}),
        ...(inverse && visual === 'accent'
          ? {
              borderColor: 'brand.on_fill_primary',
              backgroundColor: 'brand.on_fill_primary',
              color: 'brand.fill_primary',
            }
          : {}),
        ...(visual === 'primaryOnBrand'
          ? {
              borderColor: 'neutrals.on_fill_dark',
              backgroundColor: 'neutrals.on_fill_dark',
              color: 'neutrals.on_fill_light',
              '&:active:enabled': {
                bg: 'neutrals.on_fill_dark',
                color: 'neutrals.on_fill_light',
              },
              '&:focus:enabled': {
                bg: 'neutrals.fill_focused',
                color: 'neutrals.on_fill_light',
              },
            }
          : {}),
        '&[data-disabled="true"]': {
          pointerEvents: 'none',
          backgroundColor: 'neutrals.fill_disabled',
          color: 'neutrals.text_disabled',
          cursor: 'not-allowed',
          borderColor: 'neutrals.fill_disabled',
          '&:hover': {
            backgroundColor: 'neutrals.fill_disabled',
            color: 'neutrals.text_disabled',
          },
          '&:active': {
            backgroundColor: 'neutrals.fill_disabled',
            color: 'neutrals.text_disabled',
          },
          '&:focus': {
            backgroundColor: 'neutrals.fill_disabled',
            color: 'neutrals.text_disabled',
          },
        },
        ...(size === 'small'
          ? {
              fontSize: 'caption.medium_medium',
              py: 'extra_small_2',
              px: 'small',
              minHeight: 40,
            }
          : {}),
        ...(size === 'medium'
          ? {
              fontSize: 'label.medium_medium',
              lineHeight: 'label.medium_medium',
              letterSpacing: 'label.medium_medium',
              fontWeight: 'label.medium_medium',
              padding: 'extra_small_2',
              minHeight: 48,
              width: '100%',
            }
          : {}),
        ...(size === 'large'
          ? {
              fontSize: 'label.large',
              lineHeight: 'label.large',
              letterSpacing: 'label.large',
              fontWeight: 'label.large',
              px: 'extra_small_2',
              py: 18,
              minHeight: 56,
              width: '100%',
            }
          : {}),
        ...(noPadding === true
          ? {
              px: 0,
              py: 0,
              p: 0,
              minHeight: 0,
            }
          : {}),
      })} ${className ? className : ''}`}
      {...rest}
    >
      {children}
    </ComponentElement>
  );
};
